import type { PolygonWithHoles, Triangulation, Vec2 } from '../../geo/types';
import { planJson } from '../planJson';
import { decodeBase64 } from '../../core/base64';

export type PackedTrianglePlans = readonly (readonly [string, string])[];

/** A validated static plan stores topology only. Heights, normals, UVs and styles remain live. */
export interface TrianglePlans {
  /** Restored topology normally evaluates each vertex height once during emission. */
  readonly precomputed?: boolean;
  resolve(polygon: PolygonWithHoles, surface: boolean, build: () => Triangulation): Triangulation;
  edgeStations?(
    polygon: PolygonWithHoles,
    edge: number,
    build: () => readonly Vec2[]
  ): readonly Vec2[];
}

function keyOf(polygon: PolygonWithHoles, surface: boolean): string {
  return `${surface ? 'surface' : 'plane'}:${planJson(polygon)}`;
}

function pack(tri: Triangulation): string {
  const buffer = new ArrayBuffer(8 + tri.positions.length * 8 + tri.indices.length * 4);
  const header = new Uint32Array(buffer, 0, 2);
  header[0] = tri.positions.length;
  header[1] = tri.indices.length;
  new Float64Array(buffer, 8, tri.positions.length).set(tri.positions);
  new Uint32Array(buffer, 8 + tri.positions.length * 8, tri.indices.length).set(tri.indices);
  const bytes = new Uint8Array(buffer);
  const parts: string[] = [];
  for (let at = 0; at < bytes.length; at += 8192)
    parts.push(String.fromCharCode(...bytes.subarray(at, at + 8192)));
  return btoa(parts.join(''));
}

function unpack(encoded: string): Triangulation {
  const bytes = decodeBase64(encoded);
  if (bytes.length < 8) throw new Error('Incomplete triangle plan');
  const buffer = bytes.buffer;
  const header = new Uint32Array(buffer, bytes.byteOffset, 2);
  const positions = header[0]!,
    indices = header[1]!;
  if (positions % 2 || indices % 3 || 8 + positions * 8 + indices * 4 !== bytes.length)
    throw new Error('Invalid triangle plan dimensions');
  const tri = {
    positions: new Float64Array(buffer, bytes.byteOffset + 8, positions),
    indices: new Uint32Array(buffer, bytes.byteOffset + 8 + positions * 8, indices),
  };
  for (const position of tri.positions)
    if (!Number.isFinite(position)) throw new Error('Invalid triangle plan position');
  for (const index of tri.indices)
    if (index >= positions / 2) throw new Error('Invalid triangle plan index');
  return tri;
}

/** Decode within the existing bounded terrain jobs, instead of expanding every tile at load. */
export function restoreTrianglePlans(packed: PackedTrianglePlans): TrianglePlans {
  const entries = new Map<string, string | Triangulation>(packed);
  const stations = new Map<string, readonly Vec2[]>();
  const polygonKeys = new WeakMap<PolygonWithHoles, string>();
  const edgeKey = (polygon: PolygonWithHoles, edge: number) => {
    let key = polygonKeys.get(polygon);
    if (key === undefined) polygonKeys.set(polygon, (key = planJson(polygon)));
    return `edge:${edge}:${key}`;
  };
  return {
    precomputed: true,
    edgeStations(polygon, edge, build) {
      const key = edgeKey(polygon, edge);
      const cached = stations.get(key);
      if (cached) return cached;
      const known = entries.get(key);
      try {
        if (!known) return build();
        const tri = typeof known === 'string' ? unpack(known) : known;
        if (tri.indices.length || tri.positions.length < 4) return build();
        const nodes = Array.from({ length: tri.positions.length / 2 }, (_, i): Vec2 => [
          tri.positions[i * 2]!,
          tri.positions[i * 2 + 1]!,
        ]);
        stations.set(key, nodes);
        return nodes;
      } catch {
        entries.delete(key);
        return build();
      }
    },
    resolve(polygon, surface, build) {
      const key = keyOf(polygon, surface);
      const known = entries.get(key);
      if (!known) return build();
      if (typeof known !== 'string') return known;
      try {
        const tri = unpack(known);
        entries.set(key, tri);
        return tri;
      } catch {
        entries.delete(key);
        return build();
      }
    },
  };
}

export function recordTrianglePlans(): TrianglePlans & { readonly packed: PackedTrianglePlans } {
  const entries = new Map<string, string>();
  const polygonKeys = new WeakMap<PolygonWithHoles, string>();
  return {
    edgeStations(polygon, edge, build) {
      const nodes = build();
      let polygonKey = polygonKeys.get(polygon);
      if (polygonKey === undefined) polygonKeys.set(polygon, (polygonKey = planJson(polygon)));
      const key = `edge:${edge}:${polygonKey}`;
      const encoded = pack({
        positions: Float64Array.from(nodes.flat()),
        indices: new Uint32Array(),
      });
      if (entries.has(key) && entries.get(key) !== encoded)
        throw new Error('Conflicting terrain stations for one polygon edge');
      entries.set(key, encoded);
      return nodes;
    },
    resolve(polygon, surface, build) {
      const tri = build();
      const key = keyOf(polygon, surface),
        encoded = pack(tri);
      if (entries.has(key) && entries.get(key) !== encoded)
        throw new Error('Conflicting terrain refinement for one polygon');
      entries.set(key, encoded);
      return tri;
    },
    get packed() {
      return [...entries];
    },
  };
}
